<template>
  <Accordion
    class="accordion-filter"
    :defaultOpen="openAccordian"
  >
    <template v-slot:header>
      <span>{{ category }}</span>
      <SecondaryBadge
        v-if="values && values.length"
        class="selected-badge header"
      >
        <div class="selected-badge-content">
          {{ values.length }}
        </div>
      </SecondaryBadge>
    </template>
    <template v-if="showNewSelect" v-slot:content>
      <div
        class="multi-select"
        v-for="dimension in defaultDimensions"
        :key="dimension.key"
      >
        <SlideOutMultiSelect
          v-model="localValue[dimension.value]"
          :isBrand="isBrandDimension(dimension)"
          :items="filteredItems(dimension)"
          :label="getLabel(dimension)"
          :showSelectAll="!fetchingStatus[dimension.value]"
          :loading="fetchingStatus[dimension.value]"
          :shouldSortValues="!dimension.sortType"
          :disabled="isInactive(dimension)"
          :hideNoData="fetchingStatus[dimension.value]"
          :displayLimit="displayLimit"
          @input="handleInput(dimension, $event)"
          @focus="handleOpen(dimension)"
        />
      </div>
      <div v-if="displaySummary">
        <ToggleShowMore>
          <div
            class="multi-select"
            v-for="dimension in extraDimensions"
            :key="dimension.key"
          >
            <SlideOutMultiSelect
              v-model="localValue[dimension.value]"
              :items="filteredItems(dimension)"
              :label="getLabel(dimension)"
              :showSelectAll="!fetchingStatus[dimension.value]"
              :loading="fetchingStatus[dimension.value]"
              :shouldSortValues="!dimension.sortType"
              :disabled="isInactive(dimension)"
              :hideNoData="fetchingStatus[dimension.value]"
              :displayLimit="displayLimit"
              @input="handleInput(dimension, $event)"
              @focus="handleOpen(dimension)"
            />
          </div>
        </ToggleShowMore>
      </div>
    </template>
    <template v-else v-slot:content>
      <MultiSelect
        v-for="dimension in defaultDimensions"
        :key="dimension.key"
        :value="localValue[dimension.value]"
        :name="getName(dimension)"
        :label="getLabel(dimension)"
        :options="optionsMap[dimension.value]"
        :displayLimit="displayLimit"
        :error="hasDimensionError(dimension)"
        :disabled="isInactive(dimension)"
        :shouldSortValues="!dimension.sortType"
        :showTooltip="true"
        :truncateOptionsAtLength="50"
        @input="handleInput(dimension, $event)"
        @toggle="handleOpen(dimension, $event)"
      >
        <template v-slot:badge="{ badge }">
          <SecondaryBadge
            v-if="badge"
            class="selected-badge"
            :style="multiSelectBadgeStyling"
          >
            <div class="selected-badge-content">
              +{{ badge }}
            </div>
          </SecondaryBadge>
        </template>
        <template v-slot:noResults>
          <LoadingDarkIcon v-if="fetchingStatus[dimension.value]"></LoadingDarkIcon>
          {{ loadingMessage(dimension) }}
        </template>
      </MultiSelect>
      <div v-if="displaySummary">
        <ToggleShowMore>
          <MultiSelect
            v-for="dimension in extraDimensions"
            :key="dimension.key"
            :value="localValue[dimension.value]"
            :name="getName(dimension)"
            :label="dimension.key"
            :options="optionsMap[dimension.value]"
            :displayLimit="displayLimit"
            :error="hasDimensionError(dimension)"
            :shouldSortValues="!dimension.sortType"
            @input="handleInput(dimension, $event)"
            @toggle="handleOpen(dimension, $event)"
          >
            <template v-slot:badge="{ badge }">
              <SecondaryBadge
                v-if="badge"
                class="selected-badge"
                :style="multiSelectBadgeStyling"
              >
                <div class="selected-badge-content">
                  +{{ badge }}
                </div>
              </SecondaryBadge>
            </template>
            <template v-slot:noResults>
              <LoadingDarkIcon v-if="fetchingStatus[dimension.value]"></LoadingDarkIcon>
              {{ loadingMessage(dimension) }}
            </template>
          </MultiSelect>
        </ToggleShowMore>
      </div>
    </template>
  </Accordion>
</template>
<script>
import { mapState } from 'vuex';
import {
  Accordion,
  LoadingDarkIcon,
  MultiSelect,
  SecondaryBadge,
} from '@mikmak/components';
import ToggleShowMore from './ToggleShowMore';
import SlideOutMultiSelect from './SlideOutMultiSelect';
import { CommonLogger } from '../../../services/logger';
import { getDashboardFilterValues, getDemographicsFilterValues }
  from '../../../services/dashboard/eventApiService';
import { getCurrencyFromString } from '../../../services/dashboard/dashboardUtils';
import { getBenchmarksFilterValues } from '../../../services/dashboard/benchmarksService';

export default {
  props: {
    value: Object,
    category: String,
    dimensions: Array,
    dateRange: Object,
    brandIds: Array,
    portfolioBrands: Array,
    useSalesInsightsIfAvailable: Boolean,
    defaultLimit: Number,
    displayLimit: {
      type: Number,
      default: 3,
    },
    fetchDemographicsValues: Boolean,
    openAccordian: Boolean,
    hasBrandError: Boolean,
    comparisonRange: Object,
    singleSelectBrandId: String,
  },
  components: {
    Accordion,
    LoadingDarkIcon,
    MultiSelect,
    SecondaryBadge,
    ToggleShowMore,
    SlideOutMultiSelect,
  },
  data() {
    return {
      localValue: this.value,
      optionsMap: {},
      fetchingStatus: {},
      fetchedDimensionsSet: new Set(),
      multiSelectBadgeStyling: {
        pointerEvents: 'none',
        position: 'absolute',
        width: '40px',
        right: '35px',
        top: '3px',
      },
    };
  },
  mounted() {
    this.setOptionsMap();
  },
  computed: {
    ...mapState({
      activeView: state => state.main.activeView,
      activeBenchmarksView: state => state.benchmarks.activeBenchmarksView,
      showNewBrandSelector: state => state.main.showNewBrandSelector,
      showNewSelect: state => state.main.showNewSelect,
    }),
    values() {
      return Object.keys(this.localValue).reduce((vals, dimensionValue) => {
        const values = this.localValue[dimensionValue];

        return values ? vals.concat(values) : vals;
      }, []);
    },
    hasError() {
      return this.dimensions.some(this.hasDimensionError);
    },
    displaySummary() {
      return !!(this.defaultLimit && this.dimensions.length > this.defaultLimit);
    },
    defaultDimensions() {
      return this.displaySummary ? this.dimensions.slice(0, this.defaultLimit) : this.dimensions;
    },
    extraDimensions() {
      return this.displaySummary ? this.dimensions.slice(this.defaultLimit) : [];
    },
    benchmarksPayload() {
      return {
        brandId: this.singleSelectBrandId,
        initialStart: this.dateRange.start,
        initialEnd: this.dateRange.end,
        comparisonStart: this.comparisonRange.start,
        comparisonEnd: this.comparisonRange.end,
        context: this.activeBenchmarksView,
      };
    },
  },
  methods: {
    handleOpen(dimension, isOpen) {
      if ((this.showNewSelect || isOpen) && !this.hasBrandError) {
        this.getFiltersForDimension(dimension);
      }
    },
    handleInput(dimension, val) {
      // Have to check if we passed a null string value as
      // non-null key-value pairs are not allowed in the MultiSelect component
      const containsNull = val && val.some(v => v.value === 'null');

      this.localValue[dimension.value] = containsNull
        ? val.reduce((nullValues, v) => {
          const newVal = v.value === 'null' ? { key: v.key, value: null } : v;

          return nullValues.concat(newVal);
        }, [])
        : val;

      this.$emit('input', this.localValue);
    },
    hasOnlySalesGroup(dimension) {
      return dimension.groups.length === 1 && dimension.groups[0] === 'sales';
    },
    hasSalesGroup(dimension) {
      return dimension.groups.indexOf('sales') >= 0;
    },
    hasKeyValue(item) {
      const valuesToFilter = [undefined, null, ''];

      return !valuesToFilter.includes(item.value)
        && !valuesToFilter.includes(item.key);
    },
    filteredItems(dimension) {
      return this.optionsMap[dimension.value].filter(this.hasKeyValue);
    },
    assembleFilterObj(val) {
      // Have to handle passing non-null key-value pairs to the MultiSelect component
      const isUndefinedOrNull = val === undefined || val === null;
      const key = isUndefinedOrNull ? 'null' : val.toString();
      const value = isUndefinedOrNull ? 'null' : val;

      return { key, value };
    },
    async getAllFilterValues(dimension) {
      const token = await this.$auth.getTokenSilently();
      const data = this.activeView === 'Benchmarks'
        ? await this.fetchBenchmarksFilterValues(dimension, token)
        : await this.fetchBaseFilterValues(dimension, token);

      return data;
    },
    setAllFilterValues(dimensions, values) {
      this.activeView === 'Benchmarks'
        ? this.setBenchmarksFilters(dimensions, values)
        : this.setBaseFilters(dimensions, values);
    },
    setBenchmarksFilters(dimension, values) {
      const mappedValues = values?.filters?.map(val => this.assembleFilterObj(val))
        || [];

      this.optionsMap[dimension.value] = this.sortValues(dimension, mappedValues);
    },
    setBaseFilters(dimension, filterOptions = []) {
      const { snake, value } = dimension;
      const mappedFilters = filterOptions.map(filterOption => {
        const filterOptionSnakeCase = filterOption[snake];
        const filterOptionCamelCase = filterOption[value];
        const filterValue = filterOptionSnakeCase ?? filterOptionCamelCase;

        return this.assembleFilterObj(filterValue);
      });
      const sortedFilterValues = this.sortValues(dimension, mappedFilters);

      this.optionsMap[value] = sortedFilterValues;
    },
    async fetchBenchmarksFilterValues(dimension, token) {
      const payload = { ...this.benchmarksPayload, dimension: dimension.value };

      return await getBenchmarksFilterValues(payload, token);
    },
    async fetchBaseFilterValues(dimension, token) {
      const payload = {
        brandIds: encodeURIComponent(JSON.stringify(this.brandIds)),
        dimension: dimension.value,
        startDate: this.dateRange.start,
        endDate: this.dateRange.end,
      };

      if (!this.fetchDemographicsValues) {
        const useSales = this.hasOnlySalesGroup(dimension) ||
          (this.useSalesInsightsIfAvailable && this.hasSalesGroup(dimension));
        const activeView = useSales ? 'Sales Insights' : '';

        payload.activeView = activeView;

        const { data } = await getDashboardFilterValues(payload, token);

        return data;
      } else {
        const { filters } = await getDemographicsFilterValues(payload, token);

        return filters.map(f => {
          const keyValue = {};

          keyValue[dimension.value] = f;

          return keyValue;
        });
      }
    },
    async getFiltersForDimension(dimension) {
      if (this.fetchedDimensionsSet.has(dimension.value)) return;

      this.updateFetchingStatus(dimension, true);

      if (this.isBrandDimension(dimension) && !this.showNewBrandSelector) {
        this.optionsMap[dimension.value] = this.portfolioBrands;

        this.updateFetchingStatus(dimension, false);

        return;
      }

      try {
        const data = await this.getAllFilterValues(dimension);

        this.setAllFilterValues(dimension, data);
        this.fetchedDimensionsSet.add(dimension.value);
      } catch (error) {
        CommonLogger.error('Error retrieving dashboard filter values', {
          error,
        });
      } finally {
        this.updateFetchingStatus(dimension, false);
      }
    },
    loadingMessage(dimension) {
      return this.fetchingStatus[dimension.value] ? 'Loading results' : 'No results';
    },
    getName(dimension) {
      return `${dimension.value}-filter`;
    },
    getLabel(dimension) {
      let label = dimension.key;

      if (this.isInactive(dimension)) label += ' (Not available)';

      return label;
    },
    isBrandDimension(dimension) {
      return dimension.value === 'brandName';
    },
    hasDimensionError(dimension) {
      return this.isBrandDimension(dimension)
        && (!this.localValue['brandName'] || !this.localValue['brandName'].length);
    },
    isInactive(dimension) {
      return dimension.disabled;
    },
    sortValues(dimension, valuesList) {

      const values = valuesList ? [...valuesList] : [];

      if (dimension && dimension.sortType) {
        return values.sort((a, b) => {
          a = a.value;
          b = b.value;

          if (dimension.sortType === 'currency') {
            a = getCurrencyFromString(a);
            b = getCurrencyFromString(b);
          }

          if (dimension.sortType === 'custom') {
            const customOrder = dimension.itemOrder;

            a = customOrder.indexOf(a.toLowerCase());
            b = customOrder.indexOf(b.toLowerCase());
          }

          if (a === b) return 0;
          else if (a === null) return 1;
          else if (b === null) return -1;
          else
            return (a > b ? 1 : -1);
        });
      }

      return values;
    },
    setOptionsMap() {
      this.optionsMap = this.dimensions.reduce((map, dimension) => {
        const val = this.localValue[dimension.value];

        const initialValues = (val || []).map(v => {
          return {
            key: v.key,
            value: v.value === null ? 'null' : v.value,
          };
        });

        map[dimension.value] = this.sortValues(dimension, initialValues);

        return map;
      }, {});
    },
    updateFetchingStatus(dimension, newStatus) {
      this.fetchingStatus[dimension.value] = newStatus;
      this.fetchingStatus = Object.assign({}, this.fetchingStatus);
    },
  },
  watch: {
    hasError(newVal) {
      this.$emit('error', newVal);
    },
  },
};
</script>
<style scoped>
.selected-badge.header {
  position: absolute;
  right: 75px;
  width: 40px;
  height: 28px;
  margin-top: -8px;
  margin-bottom: -8px;
  pointer-events: none;
}

.selected-badge-content {
  margin: auto;
}

.accordion-filter .multi-select-wrapper {
  margin-bottom: 16px;
}

.accordion-filter .multi-select-wrapper:last-child {
  margin-bottom: 0;
}
</style>
<style>
.accordion-filter .selected-badge button.secondary {
  background: #00A6A4;
  width: 100%;
}
</style>
